"use client";
import React, { useState } from "react";
import { MessageSquare, Plus, Minus, Send, Phone } from "lucide-react";

export default function FAQsPage() {
  const appTitle = process.env.NEXT_PUBLIC_APP_TITLE;
  const appEmail = process.env.NEXT_PUBLIC_APP_EMAIL;
  const appPhone = process.env.NEXT_PUBLIC_APP_PHONE;
  
  const faqs = [
    {
      question: "How do I use a coupon code?",
      answer: "Browse our site, copy the coupon code, and apply it at checkout on the partner store’s website.",
    },
    {
      question: `Is ${process.env.NEXT_PUBLIC_APP_TITLE}  free to use?`,
      answer: "Yes, our platform is completely free for all users.",
    },
    {
      question: "How often are deals updated?",
      answer: "We update deals and coupons daily to ensure you get the latest offers.",
    },
    {
      question: "How can I contact support?",
      answer: `You can reach us via the Contact Us page or email ${process.env.NEXT_PUBLIC_APP_EMAIL}.`,
    },
  ];

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 lg:py-7">
      <div className="max-w-4xl mx-auto py-10 sm:py-16 lg:py-12 px-4 sm:px-6 lg:px-8 bg-white shadow-2xl lg:rounded-2xl">
        
        {/* Title Section */}
        <div className="mb-10 border-b pb-4 border-gray-200 text-center sm:text-left">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2 flex items-center justify-center sm:justify-start">
            <MessageSquare className="w-7 h-7 mr-3 text-primary-600 hidden sm:inline" />
            Frequently Asked Questions
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto sm:mx-0">
            Everything you need to know about using {appTitle} and saving money.
          </p>
        </div>

        {/* Accordion FAQ List */}
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div 
              key={i} 
              className={`bg-white rounded-xl transition-all duration-300 border ${
                activeIndex === i 
                  ? 'border-primary-600 shadow-xl ring-2 ring-primary-200' 
                  : 'border-gray-200 shadow-lg hover:shadow-xl'
              }`}
            >
              <button
                className="flex justify-between items-center w-full p-5 text-left"
                onClick={() => toggleFAQ(i)}
                aria-expanded={activeIndex === i}
                aria-controls={`faq-answer-${i}`}
              >
                <h2 className={`text-lg font-semibold transition-colors duration-300 ${
                  activeIndex === i ? 'text-primary-700' : 'text-gray-900'
                }`}>
                  {faq.question}
                </h2>
                {activeIndex === i ? (
                  <Minus className="w-6 h-6 text-primary-600 flex-shrink-0 transition-transform duration-300" />
                ) : (
                  <Plus className="w-6 h-6 text-gray-500 flex-shrink-0 transition-transform duration-300" />
                )}
              </button>

              <div
                id={`faq-answer-${i}`}
                className={`overflow-hidden transition-all duration-500 ease-in-out`}
                style={{
                    maxHeight: activeIndex === i ? '300px' : '0' 
                }}
              >
                <div className="p-5 pt-0">
                    <p className="text-base text-gray-700 border-t border-gray-100 pt-4">
                      {faq.answer}
                    </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-16 pt-8 border-t border-gray-200 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Still Have Questions?</h3>
            <p className="text-gray-600 mb-6">If your question wasn&apos;t answered, reach out to our support team.</p>
            
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <a 
                    href={`mailto:${appEmail}`}
                    className="flex items-center justify-center space-x-2 bg-primary-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:bg-primary-700 hover:shadow-md"
                >
                    <Send className="w-5 h-5" />
                    <span>Email Support</span>
                </a>
                <a 
                    href={`tel:${appPhone}`}
                    className="flex items-center justify-center space-x-2 bg-gray-100 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:bg-gray-200 hover:shadow-md"
                >
                    <Phone className="w-5 h-5" />
                    <span>Call Us: {appPhone}</span>
                </a>
            </div>
        </div>
      </div>
    </div>
  );
}